import { useMemo } from 'react';
import config from 'config';
import classNames from 'classnames';
import useSWR from 'swr';
import Fuse from 'fuse.js';
import { changeUrl } from '$util/index';
import type { SearchSuggestionsType } from '$util/types';
import type { SuggestionMatchBase } from '$components/composite/SearchSuggestions';
import type { SearchSuggestionsAnalyticsProps } from '../SearchSuggestionsAnalytics';
import { SearchSuggestionsAnalytics } from '../SearchSuggestionsAnalytics';

const fetcher = ({ url, type }: { url: string; type: SearchSuggestionsType }) =>
  fetch(`${url}?data=${JSON.stringify({ type })}`).then((res) => res.json());

const FUSE_OPTIONS: Fuse.IFuseOptions<SuggestionData> = {
  keys: ['name'],
  threshold: 0.3,
  includeScore: true,
  includeMatches: true,
};

const matchSuggestions = (rows: SuggestionData[], value: string): Suggestion[] => {
  if (!value) return rows.map((s) => ({ ...s, matches: [] }));
  const fuse = new Fuse(rows, FUSE_OPTIONS);
  const search = fuse.search(value);
  const suggestions = search?.map(({ item, matches = [] }) => ({ ...item, matches }));
  return suggestions;
};

interface SuggestionData {
  readonly name: string;
  readonly url: string;
}

type Suggestion = SuggestionData & SuggestionMatchBase;

type PropsSearchSuggestions = SearchSuggestionsAnalyticsProps<Suggestion>;

export interface SearchTopCardProps {
  id: string;
  type: SearchSuggestionsType;
  className?: string;
  placeholder?: string;
}

/**
 * Search component that fetches it's own data.
 * Used above the fold on pages like `cars` and `our-services`.
 */
export function SearchTopCard({ id, type, className, placeholder }: SearchTopCardProps): JSX.Element {
  const { data } = useSWR({ url: '/api/search-suggestions', type }, fetcher);
  const [suggestions, isLoadingSuggestions] = useMemo(() => [data || [], !data], [data]);

  const onSuggestionSelected: PropsSearchSuggestions['onSuggestionSelected'] = (e, { suggestion }) => {
    changeUrl({ pathname: suggestion.url });
  };

  return (
    <SearchSuggestionsAnalytics<Suggestion>
      className={classNames('SearchTopCard', className)}
      id={id}
      placeholder={placeholder}
      isLoadingSuggestions={isLoadingSuggestions}
      getSuggestions={(value) => matchSuggestions(suggestions, value)}
      getSuggestionValue={({ name }) => name}
      onSuggestionSelected={onSuggestionSelected}
      analytics={config.get('public.ga4.enabled')}
    />
  );
}
SearchTopCard.displayName = 'SearchTopCard';

export default SearchTopCard;
